<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useKeyboardNavigableList } from 'dashboard/composables/useKeyboardNavigableList';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  type: {
    type: String,
    default: 'canned',
  },
});

const emit = defineEmits(['mentionSelect']);

const { getPlainText } = useMessageFormatter();

const mentionsListContainerRef = ref(null);
const selectedIndex = ref(0);
const pointerMoved = ref(false);

const adjustScroll = () => {
  nextTick(() => {
    const container = mentionsListContainerRef.value;
    if (!container) return;
    const selectedElement = container.querySelector(
      `#mention-item-${selectedIndex.value}`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  });
};

const onSelect = () => {
  emit('mentionSelect', props.items[selectedIndex.value]);
};

useKeyboardNavigableList({
  items: computed(() => props.items),
  onSelect,
  adjustScroll,
  selectedIndex,
});

// Fall back to id, then to the item itself, so a picker that ships items without a `key`
// still gets a real comparison instead of silently degenerating to undefined !== undefined
// (which is false for every row, i.e. "nothing changed", i.e. the bug this watch fixes).
const identityOf = (item = {}) => item.key ?? item.id ?? item;

watch(
  () => props.items,
  (newItems, oldItems = []) => {
    // Snap back to the top whenever the RESULTS change — i.e. the user typed another
    // character and the list was re-filtered. Only resetting when the new list was
    // shorter (the previous behaviour) left the highlight parked on an unrelated row,
    // so the agent had to hunt for the reply they had just typed the shortcode for.
    const resultsChanged =
      newItems.length !== oldItems.length ||
      newItems.some(
        (item, index) => identityOf(item) !== identityOf(oldItems[index])
      );

    if (resultsChanged || newItems.length < selectedIndex.value + 1) {
      selectedIndex.value = 0;
      // The pointer has not moved yet for THIS list. Without resetting, a pointer left
      // resting over the box fires mouseover as the rows re-render underneath it and
      // silently drags the selection off the top match again.
      pointerMoved.value = false;
    }
  }
);

watch(selectedIndex, adjustScroll);

// The picker opens directly above the composer, so the pointer is very often already
// sitting on top of it. Honour mouseover only once the pointer has genuinely MOVED over
// this list — otherwise a stationary cursor hijacks the selection from the top match and
// Enter sends the wrong reply.
const onPointerMove = () => {
  pointerMoved.value = true;
};

const onHover = index => {
  if (!pointerMoved.value) return;
  selectedIndex.value = index;
};

const onListItemSelection = index => {
  selectedIndex.value = index;
  onSelect();
};

const variableKey = (item = {}) => {
  return props.type === 'variable' ? `{{${item.label}}}` : `/${item.label}`;
};
</script>

<template>
  <div
    ref="mentionsListContainerRef"
    class="bg-n-solid-1 p-1 rounded-xl overflow-auto absolute w-full z-20 shadow-md left-0 bottom-full max-h-[16rem] border border-solid border-n-strong mention--box"
    @mousemove="onPointerMove"
  >
    <ul class="mb-0 vertical dropdown menu">
      <template v-for="(item, index) in items" :key="item.key">
        <li
          v-if="item.folderHeader"
          class="sticky top-0 z-10 px-3 pt-2 pb-1 text-xs font-semibold text-n-slate-10 bg-n-solid-1"
        >
          {{ item.folderHeader }}
        </li>
        <woot-dropdown-item
          :id="`mention-item-${index}`"
          class="!mb-1 scroll-mt-8"
          @mouseover="onHover(index)"
        >
          <button
            class="flex rounded-lg group flex-col gap-0.5 overflow-hidden cursor-pointer items-start px-3 py-2 justify-center w-full h-full text-left hover:bg-n-alpha-black2"
            :class="{
              // the keyboard selection — the row Enter will actually insert — gets a
              // ring, not just the same tint hover uses, so it is never ambiguous which
              // reply is armed
              'bg-n-alpha-black2 ring-1 ring-inset ring-n-blue-8':
                index === selectedIndex,
            }"
            @click="onListItemSelection(index)"
          >
            <slot
              :item="item"
              :index="index"
              :selected="index === selectedIndex"
            >
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ getPlainText(item.description) }}
              </p>
              <p
                class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
                :class="{
                  'text-n-slate-12': index === selectedIndex,
                }"
              >
                {{ variableKey(item) }}
              </p>
            </slot>
          </button>
        </woot-dropdown-item>
      </template>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.mention--box {
  .dropdown-menu__item:last-child > button {
    @apply border-0;
  }
}

.canned-item__button::v-deep .button__content {
  @apply overflow-hidden text-ellipsis whitespace-nowrap;
}
</style>
